import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Snackbar,
  IconButton,
  Avatar,
  Checkbox,
  ListItemText,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  PersonAdd as PersonIcon,
  CheckCircle as CheckIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Phone as PhoneIcon,
  Person as UserIcon,
  FamilyRestroom as FamilyIcon,
  Church as ChurchIcon,
  Favorite as HeartIcon,
  Work as WorkIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { settingsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';
import api from '../../services/api';

const steps = [
  { label: 'Basic Info', icon: <PersonIcon />, step: 0 },
  { label: 'Contact Details', icon: <PhoneIcon />, step: 1 },
  { label: 'Family Details', icon: <FamilyIcon />, step: 2 },
  { label: 'Spiritual Info', icon: <ChurchIcon />, step: 3 },
  { label: 'Partner Preference', icon: <HeartIcon />, step: 4 },
  { label: 'Education & Career', icon: <WorkIcon />, step: 5 }
];

const AddNewUserWizard = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState(null);
  const [masterData, setMasterData] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profilePictureError, setProfilePictureError] = useState(false);
  
  const [formData, setFormData] = useState({
    phone: '',
    country_code: '+977',
    first_name: '',
    last_name: '',
    gender: '',
    date_of_birth: '',
    about_me: '',
    profile_for_id: '',
    marital_status_id: '',
    gotra_id: '',
    country_id: '',
    state_id: '',
    city_id: '',
    residency_status_id: '',
    you_have_id: '',
    family_type_id: '',
    manglik_family: '',
    sun_sign_id: '',
    moon_sign_id: '',
    religion_id: '',
    community_id: '',
    height_id: '',
    weight_id: '',
    diet_id: '',
    manglik_spiritual: '',
    age_from: '',
    age_to: '',
    height_from_id: '',
    height_to_id: '',
    manglik_partner: '',
    marital_status_ids: '',
    religion_ids: '',
    country_ids: '',
    qualification_ids: '',
    working_as_ids: '',
    diet_ids: '',
    qualification_category_id: '',
    qualification_id: '',
    college_name: '',
    working_as_category_id: '',
    working_as_id: '',
    working_with_category_id: '',
    working_with_id: '',
    company_name: ''
  });
  
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [workingAsOptions, setWorkingAsOptions] = useState([]);
  const [workingWithOptions, setWorkingWithOptions] = useState([]);
  
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [loadingQualifications, setLoadingQualifications] = useState(false);
  const [loadingWorkingAs, setLoadingWorkingAs] = useState(false);
  const [loadingWorkingWith, setLoadingWorkingWith] = useState(false);

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.getMasterData();
      setMasterData(response.data.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to fetch master data');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      setProfilePicture(file);
      setProfilePicturePreview(URL.createObjectURL(file));
      setProfilePictureError(false);
    }
  };

  const uploadProfilePicture = async (userIdToUpload) => {
    if (!profilePicture) {
      setProfilePictureError(true);
      toast.error('Profile picture is required');
      return null;
    }
    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('profile_photo', profilePicture);
      const response = await api.post(`/users/${userIdToUpload}/upload-profile-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.success('Profile picture uploaded successfully!');
        return response.data.data?.photo_url;
      }
      return null;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error('Failed to upload profile picture');
      return null;
    } finally {
      setUploadingPicture(false);
      setProfileDialogOpen(false);
    }
  };

  const removeProfilePicture = () => {
    setProfilePicture(null);
    setProfilePicturePreview(null);
    setProfilePictureError(false);
  };

  const loadStates = async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    setLoadingStates(true);
    try {
      const response = await settingsAPI.getStates(countryId);
      setStates(response.data.data);
    } catch (error) {
      console.error('Error loading states:', error);
    } finally {
      setLoadingStates(false);
    }
  };

  const loadCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    try {
      const response = await settingsAPI.getCities(stateId);
      setCities(response.data.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    } finally {
      setLoadingCities(false);
    }
  };

  const loadCommunities = async (religionId) => {
    if (!religionId) {
      setCommunities([]);
      return;
    }
    setLoadingCommunities(true);
    try {
      const response = await settingsAPI.getCommunitiesByReligion(religionId);
      setCommunities(response.data.data);
    } catch (error) {
      console.error('Error loading communities:', error);
    } finally {
      setLoadingCommunities(false);
    }
  };

  const loadQualificationsByCategory = async (categoryId) => {
    if (!categoryId) {
      setQualifications([]);
      return;
    }
    setLoadingQualifications(true);
    try {
      const filteredQualifications = (masterData.qualifications || []).filter(
        q => q.category_id == categoryId
      );
      setQualifications(filteredQualifications);
    } catch (error) {
      console.error('Error loading qualifications:', error);
    } finally {
      setLoadingQualifications(false);
    }
  };

  const loadWorkingAsByCategory = async (categoryId) => {
    if (!categoryId) {
      setWorkingAsOptions([]);
      return;
    }
    setLoadingWorkingAs(true);
    try {
      const filteredWorkingAs = (masterData.workingAs || []).filter(
        w => w.category_id == categoryId
      );
      setWorkingAsOptions(filteredWorkingAs);
    } catch (error) {
      console.error('Error loading working as:', error);
    } finally {
      setLoadingWorkingAs(false);
    }
  };

  const loadWorkingWithByCategory = async (categoryId) => {
    if (!categoryId) {
      setWorkingWithOptions([]);
      return;
    }
    setLoadingWorkingWith(true);
    try {
      const filteredWorkingWith = (masterData.workingWith || []).filter(
        w => w.category_id == categoryId
      );
      setWorkingWithOptions(filteredWorkingWith);
    } catch (error) {
      console.error('Error loading working with:', error);
    } finally {
      setLoadingWorkingWith(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    if (field === 'country_id') {
      setFormData({ ...formData, country_id: value, state_id: '', city_id: '' });
      loadStates(value);
    }
    if (field === 'state_id') {
      setFormData({ ...formData, state_id: value, city_id: '' });
      loadCities(value);
    }
    if (field === 'religion_id') {
      setFormData({ ...formData, religion_id: value, community_id: '' });
      loadCommunities(value);
    }
    if (field === 'qualification_category_id') {
      setFormData({ ...formData, qualification_category_id: value, qualification_id: '' });
      loadQualificationsByCategory(value);
    }
    if (field === 'working_as_category_id') {
      setFormData({ ...formData, working_as_category_id: value, working_as_id: '' });
      loadWorkingAsByCategory(value);
    }
    if (field === 'working_with_category_id') {
      setFormData({ ...formData, working_with_category_id: value, working_with_id: '' });
      loadWorkingWithByCategory(value);
    }
  };

  const handlePartnerChange = (field, value) => {
    // If value is 'open', set empty string (means no filter)
    if (value === 'open') {
      setFormData({ ...formData, [field]: '' });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleMultiSelectChange = (field, value) => {
    // Filter out 'open' - if 'open' is selected, clear all
    if (value.includes('open')) {
      setFormData({ ...formData, [field]: '' });
    } else {
      const filteredValues = value.filter(v => v && v !== '');
      setFormData({ ...formData, [field]: filteredValues.join(',') });
    }
  };

  const validateStep = () => {
    if (activeStep === 0) {
      if (!formData.phone) {
        toast.error('Phone number is required');
        return false;
      }
      if (!formData.first_name || !formData.last_name) {
        toast.error('First name and last name are required');
        return false;
      }
      if (!profilePicture) {
        toast.error('Profile picture is required');
        setProfilePictureError(true);
        return false;
      }
    }
    return true;
  };

  const saveStepData = async () => {
    if (!validateStep()) return false;
    setSaving(true);
    try {
      if (activeStep === 0 && !userId) {
        const response = await api.post('/users/create', {
          phone: formData.phone,
          country_code: formData.country_code,
          first_name: formData.first_name,
          last_name: formData.last_name,
          gender: formData.gender,
          date_of_birth: formData.date_of_birth,
          about_me: formData.about_me,
          profile_for_id: formData.profile_for_id
        });
        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to create user');
        }
        const newUserId = response.data.user_id;
        setUserId(newUserId);
        
        // Upload profile picture (mandatory)
        const photoUrl = await uploadProfilePicture(newUserId);
        if (!photoUrl) {
          throw new Error('Profile picture upload failed');
        }
        
        toast.success('User created successfully!');
        return true;
      }
      
      if (activeStep === 1 && userId) {
        await api.put(`/users/${userId}/contact`, {
          marital_status_id: formData.marital_status_id || null,
          gotra_id: formData.gotra_id || null,
          country_id: formData.country_id || null,
          state_id: formData.state_id || null,
          city_id: formData.city_id || null,
          residency_status_id: formData.residency_status_id || null
        });
        toast.success('Contact details saved!');
        return true;
      }
      
      if (activeStep === 2 && userId) {
        await api.put(`/users/${userId}/family-details`, {
          you_have_id: formData.you_have_id || null,
          family_type_id: formData.family_type_id || null,
          manglik: formData.manglik_family || null,
          sun_sign_id: formData.sun_sign_id || null,
          moon_sign_id: formData.moon_sign_id || null
        });
        toast.success('Family details saved!');
        return true;
      }
      
      if (activeStep === 3 && userId) {
        await api.put(`/users/${userId}/spiritual`, {
          religion_id: formData.religion_id || null,
          community_id: formData.community_id || null,
          height_id: formData.height_id || null,
          weight_id: formData.weight_id || null,
          diet_id: formData.diet_id || null,
          manglik: formData.manglik_spiritual || null
        });
        toast.success('Spiritual information saved!');
        return true;
      }
      
      if (activeStep === 4 && userId) {
        const partnerData = {};
        if (formData.age_from && formData.age_from !== '') partnerData.age_from = parseInt(formData.age_from);
        if (formData.age_to && formData.age_to !== '') partnerData.age_to = parseInt(formData.age_to);
        if (formData.height_from_id && formData.height_from_id !== '') partnerData.height_from_id = parseInt(formData.height_from_id);
        if (formData.height_to_id && formData.height_to_id !== '') partnerData.height_to_id = parseInt(formData.height_to_id);
        if (formData.manglik_partner && formData.manglik_partner !== '') partnerData.manglik = parseInt(formData.manglik_partner);
        if (formData.marital_status_ids && formData.marital_status_ids !== '') partnerData.marital_status_ids = formData.marital_status_ids;
        if (formData.religion_ids && formData.religion_ids !== '') partnerData.religion_ids = formData.religion_ids;
        if (formData.country_ids && formData.country_ids !== '') partnerData.country_ids = formData.country_ids;
        if (formData.qualification_ids && formData.qualification_ids !== '') partnerData.qualification_ids = formData.qualification_ids;
        if (formData.working_as_ids && formData.working_as_ids !== '') partnerData.working_as_ids = formData.working_as_ids;
        if (formData.diet_ids && formData.diet_ids !== '') partnerData.diet_ids = formData.diet_ids;
        
        if (Object.keys(partnerData).length > 0) {
          await api.put(`/users/${userId}/partner-preference`, partnerData);
        }
        toast.success('Partner preference saved!');
        return true;
      }
      
      if (activeStep === 5 && userId) {
        await api.put(`/users/${userId}/education`, {
          qualification_category_id: formData.qualification_category_id || null,
          qualification_id: formData.qualification_id || null,
          college_name: formData.college_name || null
        });
        await api.put(`/users/${userId}/profession`, {
          working_as_category_id: formData.working_as_category_id || null,
          working_as_id: formData.working_as_id || null,
          working_with_category_id: formData.working_with_category_id || null,
          working_with_id: formData.working_with_id || null,
          company_name: formData.company_name || null
        });
        toast.success('User profile completed successfully!');
        return true;
      }
      return true;
    } catch (error) {
      console.error('Error saving step:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to save data',
        severity: 'error'
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const success = await saveStepData();
    if (success) {
      if (activeStep === steps.length - 1) {
        setSnackbar({
          open: true,
          message: 'User created successfully! Redirecting...',
          severity: 'success'
        });
        setTimeout(() => {
          navigate('/users');
        }, 2000);
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const renderSelect = (label, field, options, disabled = false) => {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={formData[field] || ''}
          label={label}
          onChange={(e) => handleChange(field, e.target.value)}
        >
          <MenuItem value="">None</MenuItem>
          {options.map(opt => (
            <MenuItem key={opt.id} value={opt.id}>{opt.name || opt.status_name || opt.gotra_name}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };
  
  const renderTextField = (label, field, type = 'text', required = false) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      type={type}
      value={formData[field] || ''}
      onChange={(e) => handleChange(field, e.target.value)}
      required={required}
      InputLabelProps={type === 'date' ? { shrink: true } : {}}
    />
  );

  const normalizeOptions = (options) => {
    if (!options || !Array.isArray(options)) return [];
    return options.map(opt => ({
      id: opt.id,
      name: opt.name || opt.status_name || opt.gotra_name || opt.profile_type || 
            (opt.height_cm ? `${opt.height_cm} cm` : null) ||
            (opt.weight_kg ? `${opt.weight_kg} kg` : null) ||
            'Unknown'
    }));
  };

const getStepContent = (step) => {
  switch (step) {
    case 0:
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Step 1: Create basic user account. Profile picture is required.
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={profilePicturePreview}
                  sx={{ width: 100, height: 100, border: profilePictureError ? '3px solid red' : '3px solid #e53935' }}
                >
                  {!profilePicturePreview && <PersonIcon sx={{ fontSize: 50 }} />}
                </Avatar>
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', bottom: 0, right: 0, backgroundColor: '#e53935', color: 'white' }}
                  onClick={() => setProfileDialogOpen(true)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">Profile Picture *</Typography>
                <Typography variant="caption" color={profilePictureError ? 'error' : 'textSecondary'}>
                  {profilePictureError ? 'Profile picture is required' : 'Click the edit icon to upload'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>{renderTextField('Phone Number', 'phone', 'text', true)}</Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Profile For</InputLabel>
              <Select value={formData.profile_for_id || ''} label="Profile For" onChange={(e) => handleChange('profile_for_id', e.target.value)}>
                <MenuItem value="">None</MenuItem>
                {masterData.profileForOptions?.map(opt => (<MenuItem key={opt.id} value={opt.id}>{opt.profile_type || opt.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>{renderTextField('First Name', 'first_name', 'text', true)}</Grid>
          <Grid item xs={12} md={6}>{renderTextField('Last Name', 'last_name', 'text', true)}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Gender', 'gender', [{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }, { id: 'other', name: 'Other' }])}</Grid>
          <Grid item xs={12} md={6}>{renderTextField('Date of Birth', 'date_of_birth', 'date')}</Grid>
          <Grid item xs={12}>{renderTextField('About Me', 'about_me', 'textarea')}</Grid>
        </Grid>
      );
      
    case 1:
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}><Alert severity="info">Step 2: Add contact and family information.</Alert></Grid>
          <Grid item xs={12} md={6}>{renderSelect('Marital Status', 'marital_status_id', masterData.maritalStatus || [])}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Gotra', 'gotra_id', masterData.gotras || [])}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Country', 'country_id', masterData.countries || [])}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('State', 'state_id', states, !formData.country_id)}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('City', 'city_id', cities, !formData.state_id)}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Residency Status', 'residency_status_id', masterData.residencyStatus || [])}</Grid>
        </Grid>
      );
      
    case 2:
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}><Alert severity="info">Step 3: Add family background details.</Alert></Grid>
          <Grid item xs={12} md={6}>{renderSelect('You Have', 'you_have_id', masterData.youHaveOptions || [])}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Family Type', 'family_type_id', masterData.familyTypes || [])}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Manglik', 'manglik_family', [{ id: '1', name: 'Yes' }, { id: '0', name: 'No' }])}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Sun Sign', 'sun_sign_id', masterData.sunSigns || [])}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Moon Sign', 'moon_sign_id', masterData.moonSigns || [])}</Grid>
        </Grid>
      );
      
    case 3:
      // Normalize height and weight options for display
      const heightOptions = normalizeOptions(masterData.heights || []);
      const weightOptions = normalizeOptions(masterData.weights || []);
      
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Step 4: Add spiritual and lifestyle information.
            </Alert>
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelect('Religion', 'religion_id', masterData.religions || [])}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelect('Community', 'community_id', communities, !formData.religion_id)}
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Height</InputLabel>
              <Select
                value={formData.height_id || ''}
                label="Height"
                onChange={(e) => handleChange('height_id', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {heightOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Weight</InputLabel>
              <Select
                value={formData.weight_id || ''}
                label="Weight"
                onChange={(e) => handleChange('weight_id', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {weightOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            {renderSelect('Diet', 'diet_id', masterData.diets || [])}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelect('Manglik', 'manglik_spiritual', [
              { id: '1', name: 'Yes' },
              { id: '0', name: 'No' }
            ])}
          </Grid>
        </Grid>
      );
      
    case 4:
      const ageOptions = [];
      for (let i = 18; i <= 80; i++) ageOptions.push({ id: i, name: i.toString() });
      const partnerHeightOptions = normalizeOptions(masterData.heights || []);
      const manglikOptions = [{ id: '1', name: 'Yes' }, { id: '0', name: 'No' }];
      const maritalStatusOptions = normalizeOptions(masterData.maritalStatus || []);
      const religionOptions = normalizeOptions(masterData.religions || []);
      const countryOptions = normalizeOptions(masterData.countries || []);
      const qualificationOptions = normalizeOptions(masterData.qualifications || []);
      const professionOptions = normalizeOptions(masterData.workingAs || []);
      const dietOptions = normalizeOptions(masterData.diets || []);
      
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}><Alert severity="info">Step 5: Set partner preferences. Select "Open to all" for no preference.</Alert></Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Minimum Age</InputLabel>
              <Select value={formData.age_from || ''} label="Minimum Age" onChange={(e) => handlePartnerChange('age_from', e.target.value)}>
                <MenuItem value="">Open to all</MenuItem>
                {ageOptions.map(opt => (<MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Maximum Age</InputLabel>
              <Select value={formData.age_to || ''} label="Maximum Age" onChange={(e) => handlePartnerChange('age_to', e.target.value)}>
                <MenuItem value="">Open to all</MenuItem>
                {ageOptions.map(opt => (<MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Minimum Height</InputLabel>
              <Select value={formData.height_from_id || ''} label="Minimum Height" onChange={(e) => handlePartnerChange('height_from_id', e.target.value)}>
                <MenuItem value="">Open to all</MenuItem>
                {partnerHeightOptions.map(opt => (<MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Maximum Height</InputLabel>
              <Select value={formData.height_to_id || ''} label="Maximum Height" onChange={(e) => handlePartnerChange('height_to_id', e.target.value)}>
                <MenuItem value="">Open to all</MenuItem>
                {partnerHeightOptions.map(opt => (<MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Manglik Preference</InputLabel>
              <Select value={formData.manglik_partner || ''} label="Manglik Preference" onChange={(e) => handlePartnerChange('manglik_partner', e.target.value)}>
                <MenuItem value="">Open to all</MenuItem>
                {manglikOptions.map(opt => (<MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Preferred Marital Status</InputLabel>
              <Select
                multiple
                value={formData.marital_status_ids ? formData.marital_status_ids.split(',').filter(v => v) : []}
                label="Preferred Marital Status"
                onChange={(e) => handleMultiSelectChange('marital_status_ids', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 && <Typography color="textSecondary">Open to all</Typography>}
                    {selected.map((value) => {
                      const option = maritalStatusOptions.find(opt => String(opt.id) === String(value));
                      return (<Chip key={value} label={option?.name || value} size="small" />);
                    })}
                  </Box>
                )}
              >
                <MenuItem value="open"><em>Open to all (Clear selection)</em></MenuItem>
                {maritalStatusOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    <Checkbox checked={(formData.marital_status_ids || '').split(',').indexOf(String(option.id)) > -1} />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Preferred Religions</InputLabel>
              <Select
                multiple
                value={formData.religion_ids ? formData.religion_ids.split(',').filter(v => v) : []}
                label="Preferred Religions"
                onChange={(e) => handleMultiSelectChange('religion_ids', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 && <Typography color="textSecondary">Open to all</Typography>}
                    {selected.map((value) => {
                      const option = religionOptions.find(opt => String(opt.id) === String(value));
                      return (<Chip key={value} label={option?.name || value} size="small" />);
                    })}
                  </Box>
                )}
              >
                <MenuItem value="open"><em>Open to all (Clear selection)</em></MenuItem>
                {religionOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    <Checkbox checked={(formData.religion_ids || '').split(',').indexOf(String(option.id)) > -1} />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Preferred Countries</InputLabel>
              <Select
                multiple
                value={formData.country_ids ? formData.country_ids.split(',').filter(v => v) : []}
                label="Preferred Countries"
                onChange={(e) => handleMultiSelectChange('country_ids', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 && <Typography color="textSecondary">Open to all</Typography>}
                    {selected.map((value) => {
                      const option = countryOptions.find(opt => String(opt.id) === String(value));
                      return (<Chip key={value} label={option?.name || value} size="small" />);
                    })}
                  </Box>
                )}
              >
                <MenuItem value="open"><em>Open to all (Clear selection)</em></MenuItem>
                {countryOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    <Checkbox checked={(formData.country_ids || '').split(',').indexOf(String(option.id)) > -1} />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Preferred Qualifications</InputLabel>
              <Select
                multiple
                value={formData.qualification_ids ? formData.qualification_ids.split(',').filter(v => v) : []}
                label="Preferred Qualifications"
                onChange={(e) => handleMultiSelectChange('qualification_ids', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 && <Typography color="textSecondary">Open to all</Typography>}
                    {selected.map((value) => {
                      const option = qualificationOptions.find(opt => String(opt.id) === String(value));
                      return (<Chip key={value} label={option?.name || value} size="small" />);
                    })}
                  </Box>
                )}
              >
                <MenuItem value="open"><em>Open to all (Clear selection)</em></MenuItem>
                {qualificationOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    <Checkbox checked={(formData.qualification_ids || '').split(',').indexOf(String(option.id)) > -1} />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Preferred Professions</InputLabel>
              <Select
                multiple
                value={formData.working_as_ids ? formData.working_as_ids.split(',').filter(v => v) : []}
                label="Preferred Professions"
                onChange={(e) => handleMultiSelectChange('working_as_ids', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 && <Typography color="textSecondary">Open to all</Typography>}
                    {selected.map((value) => {
                      const option = professionOptions.find(opt => String(opt.id) === String(value));
                      return (<Chip key={value} label={option?.name || value} size="small" />);
                    })}
                  </Box>
                )}
              >
                <MenuItem value="open"><em>Open to all (Clear selection)</em></MenuItem>
                {professionOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    <Checkbox checked={(formData.working_as_ids || '').split(',').indexOf(String(option.id)) > -1} />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Preferred Diet</InputLabel>
              <Select
                multiple
                value={formData.diet_ids ? formData.diet_ids.split(',').filter(v => v) : []}
                label="Preferred Diet"
                onChange={(e) => handleMultiSelectChange('diet_ids', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 && <Typography color="textSecondary">Open to all</Typography>}
                    {selected.map((value) => {
                      const option = dietOptions.find(opt => String(opt.id) === String(value));
                      return (<Chip key={value} label={option?.name || value} size="small" />);
                    })}
                  </Box>
                )}
              >
                <MenuItem value="open"><em>Open to all (Clear selection)</em></MenuItem>
                {dietOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    <Checkbox checked={(formData.diet_ids || '').split(',').indexOf(String(option.id)) > -1} />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      );
      
    case 5:
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}><Alert severity="info">Step 6: Add education and professional information.</Alert></Grid>
          <Grid item xs={12} md={6}>{renderSelect('Qualification Category', 'qualification_category_id', masterData.qualificationCategories || [])}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Qualification', 'qualification_id', qualifications, !formData.qualification_category_id)}</Grid>
          <Grid item xs={12}>{renderTextField('College/University', 'college_name')}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Working As Category', 'working_as_category_id', masterData.workingAsCategories || [])}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Working As', 'working_as_id', workingAsOptions, !formData.working_as_category_id)}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Working With Category', 'working_with_category_id', masterData.workingWithCategories || [])}</Grid>
          <Grid item xs={12} md={6}>{renderSelect('Working With', 'working_with_id', workingWithOptions, !formData.working_with_category_id)}</Grid>
          <Grid item xs={12}>{renderTextField('Company Name', 'company_name')}</Grid>
        </Grid>
      );
    default: return null;
  }
};

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const isLastStep = activeStep === steps.length - 1;

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/users')} sx={{ mb: 2, color: '#e53935' }}>Back to Users</Button>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Avatar sx={{ bgcolor: '#e53935' }}><PersonIcon /></Avatar>
          <Typography variant="h4" fontWeight="bold">Add New User - Step {activeStep + 1} of {steps.length}</Typography>
        </Box>
        {userId && <Alert severity="success" sx={{ mb: 2 }}>User created successfully! User ID: {userId}</Alert>}
        <Stepper activeStep={activeStep} sx={{ mb: 4, overflow: 'auto' }}>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel StepIconComponent={() => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {index < activeStep ? <CheckIcon sx={{ color: '#4caf50' }} /> : index === activeStep ? <Avatar sx={{ width: 24, height: 24, bgcolor: '#e53935', fontSize: 12 }}>{index + 1}</Avatar> : <Avatar sx={{ width: 24, height: 24, bgcolor: '#ccc', fontSize: 12 }}>{index + 1}</Avatar>}
                </Box>
              )}>
                <Typography variant="caption" display="block">{step.label}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        {saving && <LinearProgress sx={{ mb: 2 }} />}
        <Card><CardContent>{getStepContent(activeStep)}</CardContent></Card>
        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={handleBack} disabled={activeStep === 0 || saving} startIcon={<PrevIcon />}>Previous</Button>
          <Button variant="contained" onClick={handleNext} disabled={saving} endIcon={!isLastStep ? <NextIcon /> : null} sx={{ background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)', '&:hover': { background: 'linear-gradient(135deg, #c62828 0%, #1565c0 100%)' } }}>
            {saving ? <CircularProgress size={24} color="inherit" /> : isLastStep ? 'Complete' : 'Next'}
          </Button>
        </Box>
      </Paper>
      
      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box display="flex" justifyContent="space-between" alignItems="center"><Typography variant="h6">Profile Picture (Required)</Typography><IconButton onClick={() => setProfileDialogOpen(false)}><CloseIcon /></IconButton></Box></DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {profilePicturePreview ? (
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar src={profilePicturePreview} sx={{ width: 150, height: 150, mx: 'auto', mb: 2 }} />
                <IconButton sx={{ position: 'absolute', top: 0, right: 0, backgroundColor: 'white' }} onClick={removeProfilePicture}><DeleteIcon color="error" /></IconButton>
              </Box>
            ) : (
              <Avatar sx={{ width: 150, height: 150, mx: 'auto', mb: 2, bgcolor: '#e53935' }}><PersonIcon sx={{ fontSize: 80 }} /></Avatar>
            )}
            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} sx={{ mt: 2 }}>Choose Image<input type="file" hidden accept="image/*" onChange={handleProfilePictureChange} /></Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>Square image, max 5MB. JPG, PNG or GIF (Required)</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => setProfileDialogOpen(false)} variant="contained" sx={{ background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)' }}>Done</Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddNewUserWizard;